import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Inbox, Clock, CheckCircle2, XCircle, AlertTriangle, ExternalLink, Copy, X } from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/constants';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

const STATUS_TONE = {
  nueva:      { color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE', label: 'Nueva' },
  verificada: { color: '#7C3AED', bg: '#F3F0FF', border: '#DDD6FE', label: 'Verificada' },
  en_proceso: { color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', label: 'En proceso' },
  respondida: { color: '#065F46', bg: '#ECFDF5', border: '#A7F3D0', label: 'Respondida' },
  rechazada:  { color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB', label: 'Rechazada' },
  vencida:    { color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', label: 'Vencida' },
};

export default function Arco() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [hasPrivacy, setHasPrivacy] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('todas');

  const load = async () => {
    setLoading(true);
    try {
      const [m, list, docs] = await Promise.all([api.get('/arco/meta'), api.get('/arco/requests'), api.get('/documents')]);
      setMeta(m.data); setItems(list.data);
      setHasPrivacy(docs.data.some((d) => d.doc_type === 'privacy_policy'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'todas') return items;
    if (filter === 'abiertas') return items.filter((r) => ['nueva', 'verificada', 'en_proceso'].includes(r.status));
    return items.filter((r) => r.status === filter);
  }, [items, filter]);

  const publicUrl = meta && `${window.location.origin}${meta.public_url}`;

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando bandeja…</div>;

  const openCount = items.filter((r) => ['nueva', 'verificada', 'en_proceso'].includes(r.status)).length;
  const overdueCount = items.filter((r) => r.status === 'vencida').length;

  const answered = items.filter((r) => r.status === 'respondida').length;

  return (
    <MissionShell
      mission={MISSIONS.arco}
      idPrefix="arco-mission"
      prereq={!hasPrivacy ? { message: 'Tu política de privacidad debe informar cómo ejercer estos derechos y enlazar a tu formulario público. Créala primero.', to: '/app/documentos', cta: 'Ir a Documentos legales' } : null}
      doHint={openCount > 0
        ? `Siguiente acción: tienes ${openCount} solicitud${openCount === 1 ? '' : 'es'} abierta${openCount === 1 ? '' : 's'}. Ábrelas y respóndelas antes de su vencimiento.`
        : 'Sin solicitudes abiertas. Asegúrate de que el enlace a tu formulario público esté visible en tu sitio web y en tu política de privacidad.'}
      evidence={[
        { label: 'Solicitudes recibidas', value: items.length, tone: 'ok' },
        { label: 'Respondidas', value: `${answered} de ${items.length}`, tone: 'ok' },
        { label: 'Vencidas', value: overdueCount, tone: overdueCount ? 'bad' : 'ok' },
      ]}
      evidenceNote="Cada solicitud guarda su fecha de ingreso, verificación de identidad, plazo y respuesta enviada."
    >
    <div className="space-y-6" data-testid="arco-page">
      <div className="border-b border-[#E5E7EB] pb-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[15px] font-medium text-[#1C2329]">{items.length} solicitud{items.length === 1 ? '' : 'es'} ARCO+</div>
            <div className="text-[13px] text-[#6B7280]">{openCount} abiertas · {overdueCount} vencidas</div>
          </div>
        </div>
        <div className="bg-[#FAFBFC] border border-[#E5E7EB] px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">URL pública del Portal</div>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-[13px] font-mono text-[#143A34] hover:underline inline-flex items-center gap-1" data-testid="arco-public-link">
              {publicUrl} <ExternalLink size={11} />
            </a>
          </div>
          <button className="pv-btn-secondary text-[12px] flex items-center gap-1.5"
            onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('URL copiada'); }}>
            <Copy size={12} /> Copiar
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {['todas', 'abiertas', 'nueva', 'verificada', 'en_proceso', 'respondida', 'rechazada', 'vencida'].map((k) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`text-[12px] px-2.5 py-1 border ${filter === k ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#FAFBFC]'}`}>
            {k === 'todas' ? 'Todas' : k === 'abiertas' ? 'Abiertas' : STATUS_TONE[k]?.label || k}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center">
          <Inbox size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
          <div className="mt-3 text-lg font-semibold text-[#1C2329]">Sin solicitudes con esos filtros</div>
          <div className="text-sm text-[#6B7280] mt-1">Comparta la URL pública para empezar a recibir solicitudes.</div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="pv-table" data-testid="arco-table">
            <thead><tr>
              <th style={{ width: 100 }}>Estado</th>
              <th style={{ width: 120 }}>SLA</th>
              <th>Solicitante</th>
              <th>Tipo</th>
              <th style={{ width: 130 }}>Recibida</th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <ArcoRow key={r.id} r={r} meta={meta} onOpen={() => setSelected(r)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ArcoDetail req={selected} meta={meta} onClose={() => setSelected(null)}
          onSaved={async () => { setSelected(null); await load(); }} />
      )}
    </div>
    </MissionShell>
  );
}

function ArcoRow({ r, meta, onOpen }) {
  const tone = STATUS_TONE[r.status] || STATUS_TONE.nueva;
  const daysLeft = slaDaysLeft(r.sla_deadline);
  const overdue = daysLeft < 0 || r.status === 'vencida';
  const critical = daysLeft >= 0 && daysLeft <= 3;
  return (
    <tr onClick={onOpen} className="cursor-pointer" data-testid={`arco-row-${r.id}`}>
      <td>
        <span className="text-[11px] font-medium px-1.5 py-0.5 border" style={{ color: tone.color, backgroundColor: tone.bg, borderColor: tone.border }}>
          {tone.label}
        </span>
      </td>
      <td>
        {['respondida', 'rechazada'].includes(r.status) ? (
          <span className="text-[11.5px] text-[#6B7280]">—</span>
        ) : (
          <span className={`inline-flex items-center gap-1 text-[11.5px] font-medium ${overdue ? 'text-[#DC2626]' : critical ? 'text-[#B45309]' : 'text-[#065F46]'}`}>
            {overdue ? <AlertTriangle size={11} /> : <Clock size={11} />}
            {overdue ? `${Math.abs(daysLeft)}d vencida` : `${daysLeft}d restantes`}
          </span>
        )}
      </td>
      <td>
        <div className="text-[13px] font-medium text-[#1C2329]">{r.requester_name}</div>
        <div className="text-[11px] text-[#6B7280]">{r.requester_email}</div>
      </td>
      <td className="text-[12.5px]">{meta.types[r.request_type] || r.request_type}</td>
      <td className="text-[11.5px] text-[#6B7280]">{formatDateTime(r.created_at)}</td>
    </tr>
  );
}

function slaDaysLeft(deadlineIso) {
  if (!deadlineIso) return 15;
  const now = new Date();
  const end = new Date(deadlineIso);
  return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
}

function ArcoDetail({ req, meta, onClose, onSaved }) {
  const [status, setStatus] = useState(req.status);
  const [response, setResponse] = useState(req.response_text || '');
  const [notes, setNotes] = useState(req.internal_notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/arco/requests/${req.id}`, {
        status, response_text: response || null, internal_notes: notes || null,
      });
      toast.success('Actualizado');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const daysLeft = slaDaysLeft(req.sla_deadline);
  const overdue = daysLeft < 0;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#E5E7EB] max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="arco-detail">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">
              Referencia {req.id.slice(0, 8)} · {formatDateTime(req.created_at)}
            </div>
            <div className="text-[15px] font-semibold text-[#1C2329] mt-1">{meta.types[req.request_type] || req.request_type}</div>
            <div className="text-[12.5px] text-[#374151] mt-1">
              {req.requester_name} · <span className="font-mono">{req.requester_email}</span>
              {req.requester_rut && <> · RUT {req.requester_rut}</>}
            </div>
            <div className={`mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium ${overdue ? 'text-[#DC2626]' : daysLeft <= 3 ? 'text-[#B45309]' : 'text-[#065F46]'}`}>
              {overdue ? <AlertTriangle size={11} /> : <Clock size={11} />}
              {overdue ? `SLA vencido hace ${Math.abs(daysLeft)} días` : `SLA vence en ${daysLeft} días hábiles`}
              {req.verified_at && <span className="ml-2 text-[#7C3AED]">· Identidad verificada</span>}
              {!req.verified_at && <span className="ml-2 text-[#B45309]">· Pendiente de verificar identidad</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium mb-1">Descripción</div>
            <div className="border border-[#E5E7EB] bg-[#FAFBFC] p-3 text-[13px] text-[#374151] whitespace-pre-wrap">{req.description}</div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[#374151] mb-1.5">Estado</label>
            <select className="pv-input max-w-xs" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="arco-status">
              {Object.entries(meta.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[#374151] mb-1.5">
              Respuesta al titular {['respondida', 'rechazada'].includes(status) && <span className="text-[#DC2626]">*</span>}
            </label>
            <textarea className="pv-input" rows={6}
              value={response} onChange={(e) => setResponse(e.target.value)}
              placeholder="Escriba la respuesta que se enviará por correo al titular al guardar."
              data-testid="arco-response" />
            <div className="mt-1 text-[11px] text-[#6B7280]">Se envía como correo al marcar estado &ldquo;Respondida&rdquo; o &ldquo;Rechazada&rdquo;.</div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[#374151] mb-1.5">Notas internas</label>
            <textarea className="pv-input" rows={2}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Solo visible para el equipo interno." />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-between bg-white">
          <button className="pv-btn-secondary" onClick={onClose}>Cerrar</button>
          <button className="pv-btn-primary flex items-center gap-1.5" onClick={save} disabled={saving} data-testid="arco-save">
            {status === 'respondida' ? <CheckCircle2 size={13} /> : status === 'rechazada' ? <XCircle size={13} /> : null}
            {saving ? 'Guardando…' : 'Guardar y notificar'}
          </button>
        </div>
      </div>
    </div>
  );
}
